import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";

/**
 * How Facetra Works — canonical answer page for AI engines and buyers
 * asking "how does an AI jewellery design tool actually work?".
 * Structured Q→A + numbered stages so GEO citation is clean.
 */
export default function HowItWorks() {
  return (
    <LegalLayout
      title="How Facetra Works"
      lastUpdated="February 13, 2026"
      testId="page-how-it-works"
    >
      <p>
        Facetra converts a hand-drawn jewellery sketch into a client-ready render, multi-view
        technical sheet, live-gold-rate Bill of Materials, and downloadable 3D mesh — all in a
        single afternoon. Below is the exact seven-stage workflow an atelier follows, from the
        moment a designer sketches on paper to the moment the CAD bench opens the file.
      </p>

      <h2>Who is this workflow for?</h2>
      <p>
        Facetra is used by Indian jewellery ateliers and independent designers who work in
        traditional styles (<Link to="/glossary">Jadau, Polki, Kundan, Meenakari, Temple,
        Rajwadi</Link>) as well as modern halo, solitaire and cocktail pieces. It replaces
        a 4–7 day sketch-to-CAD handoff with a same-day workflow.
      </p>

      <h2>The seven-stage workflow</h2>

      <h3>Stage 1 — Sketch</h3>
      <p>
        Start with any hand-drawn sketch. Snap a photograph of the paper (top-down, natural
        light) and upload — or describe the piece in a text prompt if you don't sketch. Facetra
        accepts English, Hindi transliterations and mixed vocabulary (e.g. <em>"jadau bali with
        polki border and meena work on the reverse"</em>).
      </p>

      <h3>Stage 2 — Photoreal render</h3>
      <p>
        Facetra generates a photoreal front-view render preserving every design decision — prong
        count, gemstone shape, jaali density, silhouette. Uses Google Gemini Nano Banana under
        the hood, but with prompt engineering tuned specifically to jewellery craft vocabulary
        so the output looks manufacturable, not decorative-AI.
      </p>

      <h3>Stage 3 — Lasso refinement</h3>
      <p>
        Not perfect on the first try? Mark any region of the render with the lasso tool, describe
        the change (<em>"replace the centre stone with an emerald-cut"</em>, <em>"add temple
        motifs on the shoulder"</em>), and Facetra re-renders only that region. The rest of the
        design stays pixel-locked.
      </p>

      <h3>Stage 4 — Multi-view sheet</h3>
      <p>
        Generate the technical sheet the CAD bench needs: consistent 0° / 45° / 90° / 180° views
        of the same piece. Silhouette is reference-locked across views so the model-maker sees
        the same geometry from every angle — no accidental proportion drift.
      </p>

      <h3>Stage 5 — CAD-ready mode</h3>
      <p>
        Strip the stones, enamel, plating and polish from any render to get the bare-metal
        pre-polish casting reference. Plain-gold pieces are auto-detected and preserved without
        hallucinated stone sockets. This is the reference the CAD operator models against.
      </p>

      <h3>Stage 6 — Bill of Materials + live gold rate</h3>
      <p>
        Facetra generates a machine-readable BOM: weight breakdown (yellow gold / diamonds /
        gemstones / enamel), estimated cost at the current 22kt gold rate, karigar hours, and
        setting notes. The gold rate is refreshed every hour so client quotes are never stale.
        Exportable as CSV.
      </p>

      <h3>Stage 7 — 3D mesh + CAD kit download</h3>
      <p>
        Feed 2–4 multi-view images into the 3D mesh generator and receive a downloadable model
        in GLB / STL / OBJ / FBX / USDZ / 3MF. The full CAD kit zip includes:
      </p>
      <ul>
        <li>The multi-view sheet as separate PNG files</li>
        <li>The CAD-ready bare-metal render</li>
        <li>The BOM as CSV + JSON</li>
        <li>The 3D mesh (GLB, plus any format you export to)</li>
        <li>A README with wall-thickness cheatsheet (DMLS / SLA / FDM / lost-wax) and
          one-sided shell workflow for temple / animal / relief pieces</li>
      </ul>
      <p>
        Your CAD operator opens the zip in Rhino / MatrixGold, retopologizes for casting, and
        sends to CAM.
      </p>

      <h2>What does a full workflow cost?</h2>
      <p>
        For one design: 1 render + 4 credits for the multi-view sheet + 1 credit for CAD-ready +
        1 credit for the BOM + 15 credits for the Standard 3D mesh (or 25 for HD) =
        <strong> ~32 credits per finished CAD kit</strong>. At the Atelier pack rate (₹12/credit)
        that's ~₹384 per piece — significantly less than a single hour of a CAD operator's time.
      </p>

      <h2>Frequently asked questions</h2>

      <h3>Do I need to know CAD software to use Facetra?</h3>
      <p>
        No. Facetra runs entirely in the browser. Your CAD operator uses Rhino / MatrixGold /
        JewelCAD as before — Facetra just gives them a much cleaner, faster starting point.
      </p>

      <h3>Can Facetra generate CAD files directly?</h3>
      <p>
        Facetra generates a 3D mesh (GLB / STL / OBJ / FBX) which is a <em>reference</em>, not a
        cast-ready manifold model. Your CAD operator retopologizes the mesh in their CAD tool
        before sending to CAM. This is the standard workflow for AI-generated 3D output — no
        AI tool produces direct-to-cast CAD files today.
      </p>

      <h3>What jewellery styles does Facetra support?</h3>
      <p>
        Traditional Indian: Jadau, Polki, Kundan, Meenakari, Temple, Rajwadi. Modern:
        solitaire, halo, cluster, tennis, cocktail. Full vocabulary in the{" "}
        <Link to="/glossary">Facetra glossary</Link>.
      </p>

      <h3>How does Facetra compare to Midjourney or DALL·E for jewellery?</h3>
      <p>
        General-purpose AI image generators produce beautiful but non-manufacturable output.
        They cannot generate multi-view consistency, cannot produce a Bill of Materials,
        cannot export 3D meshes, and lack jewellery-craft vocabulary. Facetra is a purpose-built
        workflow that produces the exact deliverables a CAD bench needs.
      </p>

      <h3>Is my design confidential?</h3>
      <p>
        Yes. Every render lives in your private per-user storage bucket. Nothing is public
        unless you explicitly export or share it. Full details in the{" "}
        <Link to="/privacy">Facetra privacy policy</Link>.
      </p>

      <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 not-prose">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Ready to start</div>
        <h3 className="font-display text-2xl mb-3">Try Facetra with free trial credits</h3>
        <p className="text-sm text-white/70 mb-4">
          Every new account includes free credits so you can generate your first render, multi-view
          sheet and CAD kit before deciding whether to upgrade.
        </p>
        <Link
          to="/signup"
          data-testid="how-it-works-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
        >
          Sign up free →
        </Link>
      </div>
    </LegalLayout>
  );
}
